import React, { useState, useEffect } from "react";
import { View, StyleSheet, TextInput, Text, Platform } from "react-native";

import moment from "moment";

import Dialog from "./dialog";
import MoodsList from "./mood/MoodsList";
import ManaButton from "./ManaButton";
import ImageComponent from "./ImageComponent";

import AppTheme from "../AppTheme";
import SettingsHelper from "../more/SettingsHelper";
import SetupConfig from "../home/cards/onboarding/SetupConfig";
import { isAndroid } from "../../../helpers/utils";

export default function JournalDialog({
  item,
  type,
  controller = () => {},
  close = () => {},
  onJournalComplete,
  isViewOnly = false,
}) {
  if (!item) return;

  const [note, setNote] = useState(item.note || item.text);
  const [mood, setMood] = useState(item.mood);
  const [hours, setHours] = useState("");
  const [date, setDate] = useState("");
  const elementName = item.name || item.activity?.name;
  const elementIcon = item.icon || item.activity?.icon;

  useEffect(() => {
    setIntervalText();
  }, []);

  const saveJournalNote = () => {
    if (isViewOnly) return;

    controller.tracking.track("Edit Notes");
    item.note = note;
    item.mood = mood;

    if (!["timeslot", "reflection", "report"].includes(type)) return;

    // For type == "timeslot"
    let action = {
      action: "save",
      target: "journal_note",
      timeslot_id: item.id,
      note: note,
      mood: mood,
    };

    if (type === "reflection" || type === "report") {
      action = {
        action: "save",
        target: "gem",
        item_id: item.item_id,
        time_unit: item.time_unit,
        day: item.day,
        item_type: item.item_type,
        gem_id: item.gem_id,
        hours: item.hours,
        note: note,
        mood: mood,
      };
    }

    controller.apiService.cardAction(action).then((data) => {
      _updateSetupProgress(controller);

      if (onJournalComplete) {
        onJournalComplete({ gem_id: data.id, mood, text: note });
      } else {
        close();
      }
    });
  };

  const _updateSetupProgress = (controller) => {
    // Save current step in settings for checklist display
    const key = SetupConfig.reflection.name;
    const settings = SettingsHelper.getCurrentSetupProgress(controller);

    if (!settings[key]) {
      SettingsHelper.updateSetupProgress(controller, { [key]: true });
    }
  };

  const setIntervalText = () => {
    // Comparison card
    if (type == "reflection") {
      const endTimeModel = moment(item.day);
      setDate(endTimeModel.format("MMM DD, YYYY"));
    } else {
      const startTime = moment(item.time).format("hh:mma");
      const endTimeModel = moment(item.endDate);

      setHours([startTime, endTimeModel.format("hh:mma")].join(" - "));
      setDate(endTimeModel.format("MMM DD"));
    }
  };

  return (
    <Dialog.Container
      onClose={close}
      showCloseButton
      style={styles.container}
      headerStyle={{ margin: 0 }}
      contentStyle={{
        paddingTop: 0,
        marginTop: 0,
        paddingBottom: 20,

        ...Platform.select({
          android: {
            maxHeight: 280,
          },
        }),
      }}
    >
      <View style={styles.header}>
        <View style={styles.headerRow}>
          {elementIcon ? (
            <ImageComponent style={styles.icon} icon={elementIcon} />
          ) : null}
          {elementName ? (
            <Text style={styles.itemName}>{elementName}</Text>
          ) : null}
        </View>
        <View
          style={[
            styles.headerRow,
            {
              marginLeft: 28,
            },
          ]}
        >
          <Text style={styles.intervalText}>{date}</Text>
          {hours ? <View style={styles.separator} /> : null}
          <Text style={styles.intervalText}>{hours}</Text>
        </View>
      </View>

      <MoodsList
        selectedMood={mood}
        onSelect={(m) => {
          if (!isViewOnly) setMood(m);
        }}
      />

      <View style={styles.messageContainer}>
        <TextInput
          name={"journalEdit"}
          style={styles.input}
          autoFocus={true}
          multiline={true}
          numberOfLines={isAndroid ? 2 : 6}
          value={note}
          onChangeText={(text) => {
            if (!isViewOnly) setNote(text);
          }}
          placeholderTextColor="#9C9EB9"
          placeholder="Write some notes for yourself..."
        />
      </View>

      <View style={styles.buttons}>
        <ManaButton
          style={styles.cancelButton}
          text={isViewOnly ? "Close" : "Cancel"}
          styleText={styles.cancelButtonText}
          onPress={close}
        />

        {isViewOnly ? null : (
          <ManaButton
            disabled={!mood}
            style={[styles.journalButton, !mood && styles.disabled]}
            text={item.note ? "Update" : "Journal"}
            styleText={styles.journalButtonText}
            onPress={saveJournalNote}
          />
        )}
      </View>
    </Dialog.Container>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flex: 1,
  },
  containerHeader: {
    margin: 0,
  },
  close: {
    position: "absolute",
    top: 5,
    right: 5,
  },
  messageContainer: {
    marginTop: 20,
  },
  input: {
    backgroundColor: "#F9F9F9",
    borderRadius: 4,
    padding: 10,
    marginBottom: 30,

    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: "#2D3142",
  },
  buttons: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    flexDirection: "row",
  },
  cancelButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 115,
    height: 36,
    backgroundColor: "#F4F6FA",
  },
  cancelButtonText: {
    color: AppTheme.DEFAULT_COLOR,
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
  },
  journalButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 115,
    height: 36,
    backgroundColor: AppTheme.DEFAULT_COLOR,
  },
  journalButtonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: "#FFF",
  },
  disabled: {
    backgroundColor: "#818181",
  },
  header: {
    paddingVertical: 12,
    paddingRight: 12.5,
    marginRight: 20,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  icon: {
    width: 20,
    height: 20,
    marginRight: 8,
  },
  itemName: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    flex: 1,
  },
  intervalText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "500",
    fontSize: 12,
    lineHeight: 15,
    letterSpacing: 0.175,
    color: AppTheme.almostBlack,
    textAlign: "right",
  },
  separator: {
    width: 3,
    height: 3,
    borderRadius: 3,
    marginHorizontal: 4,
    backgroundColor: AppTheme.almostBlack,
  },
});
